import {
  AttachFile as DocumentIcon,
  Description as DescriptionIcon,
  Gavel as TenderIcon,
  Info as InfoIcon,
  Inventory as ProductIcon,
  Science as TrackIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { researchApi, ResearchProject } from "../../services/researchApi";
import DocumentsPanel from "./shared/DocumentsPanel";

// ── Types for context fields ──────────────────────────────────────────

interface TenderContext {
  tender_title?: string;
  tender_description?: string;
  role?: "prime_bidder" | "consortium_member" | "supplier" | "";
  role_description?: string;
}

interface ProductServiceContext {
  product_name?: string;
  product_description?: string;
}

interface ProjectContext {
  schema_version: string;
  description?: string;
  tender?: TenderContext;
  product_service?: ProductServiceContext;
  [key: string]: unknown;
}

// ── Helpers ───────────────────────────────────────────────────────────

const ROLE_OPTIONS: { value: string; label: string; description: string }[] = [
  {
    value: "prime_bidder",
    label: "Prime / Main Bidder",
    description: "You are leading the bid as the prime contractor",
  },
  {
    value: "consortium_member",
    label: "Consortium Member",
    description: "You are participating as part of a consortium or team bid",
  },
  {
    value: "supplier",
    label: "Supplier / Subcontractor",
    description: "You are supplying products or services to a bidder",
  },
];

const PROJECT_TYPE_LABELS: Record<string, string> = {
  tender: "Tender / Bid",
  product_service: "Product / Service",
  custom: "Custom",
};

const TRACK_TYPE_LABELS: Record<string, string> = {
  find_competitors: "Find Competitors",
  find_customers: "Find Customers & Leads",
  find_partners: "Find Partners & Suppliers",
};

// ── Reusable section wrapper ──────────────────────────────────────────

const Section: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ icon, title, children, action }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      mb: 3,
      backgroundColor: "background.paper",
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Paper>
);

// ── Component ─────────────────────────────────────────────────────────

interface ProjectOverviewProps {
  project: ResearchProject;
  onProjectUpdated?: (project: ResearchProject) => void;
}

const ProjectOverview: React.FC<ProjectOverviewProps> = ({
  project,
  onProjectUpdated,
}) => {
  const { hasFeature } = useAuth();
  const ctx = (project.context || {}) as ProjectContext;

  // ── Local state for editable fields ──
  const [description, setDescription] = useState(ctx.description || "");
  const [tenderTitle, setTenderTitle] = useState(
    ctx.tender?.tender_title || "",
  );
  const [tenderDescription, setTenderDescription] = useState(
    ctx.tender?.tender_description || "",
  );
  const [role, setRole] = useState(ctx.tender?.role || "");
  const [roleDescription, setRoleDescription] = useState(
    ctx.tender?.role_description || "",
  );
  const [productName, setProductName] = useState(
    ctx.product_service?.product_name || "",
  );
  const [productDescription, setProductDescription] = useState(
    ctx.product_service?.product_description || "",
  );
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Reset local state when project changes
  useEffect(() => {
    const c = (project.context || {}) as ProjectContext;
    setDescription(c.description || "");
    setTenderTitle(c.tender?.tender_title || "");
    setTenderDescription(c.tender?.tender_description || "");
    setRole(c.tender?.role || "");
    setRoleDescription(c.tender?.role_description || "");
    setProductName(c.product_service?.product_name || "");
    setProductDescription(c.product_service?.product_description || "");
    setDirty(false);
  }, [project]);

  // Mark dirty on any field change
  const update = useCallback(
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
      (value: T) => {
        setter(value);
        setDirty(true);
      },
    [],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const updatedContext: ProjectContext = {
        ...ctx,
        schema_version: ctx.schema_version || "1.0",
        description,
      };

      if (project.project_type === "tender") {
        updatedContext.tender = {
          tender_title: tenderTitle,
          tender_description: tenderDescription,
          role: role as TenderContext["role"],
          role_description: roleDescription,
        };
      }

      if (project.project_type === "product_service") {
        updatedContext.product_service = {
          product_name: productName,
          product_description: productDescription,
        };
      }

      const updated = await researchApi.updateProject(project.id, {
        context: updatedContext,
      });
      setDirty(false);
      onProjectUpdated?.(updated);
    } catch (err) {
      console.error("Failed to save project context:", err);
    } finally {
      setSaving(false);
    }
  }, [
    ctx,
    description,
    tenderTitle,
    tenderDescription,
    role,
    roleDescription,
    productName,
    productDescription,
    project,
    onProjectUpdated,
  ]);

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      {/* ── Project Metadata ── */}
      <Section
        icon={<InfoIcon sx={{ fontSize: 20, color: "text.secondary" }} />}
        title="Project Details"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "140px 1fr",
            gap: 1.5,
            alignItems: "baseline",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Type
          </Typography>
          <Chip
            label={
              PROJECT_TYPE_LABELS[project.project_type] || project.project_type
            }
            size="small"
            sx={{ width: "fit-content" }}
          />

          <Typography variant="body2" color="text.secondary">
            Status
          </Typography>
          <Chip
            label={project.status}
            size="small"
            color={project.status === "active" ? "success" : "default"}
            sx={{ width: "fit-content" }}
          />

          <Typography variant="body2" color="text.secondary">
            Created by
          </Typography>
          <Typography variant="body2">{project.created_by_username}</Typography>

          <Typography variant="body2" color="text.secondary">
            Created
          </Typography>
          <Typography variant="body2">
            {new Date(project.created_at).toLocaleDateString()}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Last updated
          </Typography>
          <Typography variant="body2">
            {new Date(project.updated_at).toLocaleDateString()}
          </Typography>
        </Box>
      </Section>

      {/* ── Project Description (all types) ── */}
      <Section
        icon={
          <DescriptionIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        }
        title="Project Description"
      >
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          placeholder="Describe the overall goal and scope of this research project..."
          value={description}
          onChange={(e) => update(setDescription)(e.target.value)}
        />
      </Section>

      {/* ── Tender-specific: Tender Details ── */}
      {project.project_type === "tender" && (
        <Section
          icon={<TenderIcon sx={{ fontSize: 20, color: "text.secondary" }} />}
          title="Tender Details"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField
              fullWidth
              label="Tender Title"
              placeholder="e.g., NATO AWACS Replacement Program"
              value={tenderTitle}
              onChange={(e) => update(setTenderTitle)(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              label="Tender Description"
              placeholder="Paste or summarize the tender description..."
              value={tenderDescription}
              onChange={(e) => update(setTenderDescription)(e.target.value)}
            />

            <FormControl fullWidth>
              <InputLabel id="role-label">Your Role</InputLabel>
              <Select
                labelId="role-label"
                value={role}
                label="Your Role"
                onChange={(e) => update(setRole)(e.target.value)}
              >
                <MenuItem value="">
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    fontStyle="italic"
                  >
                    Not specified
                  </Typography>
                </MenuItem>
                {ROLE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        {opt.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              label="Role Description"
              placeholder="Describe your role, capabilities, and what you bring to this tender..."
              value={roleDescription}
              onChange={(e) => update(setRoleDescription)(e.target.value)}
            />
          </Box>
        </Section>
      )}

      {/* ── Product/Service-specific ── */}
      {project.project_type === "product_service" && (
        <Section
          icon={<ProductIcon sx={{ fontSize: 20, color: "text.secondary" }} />}
          title="Product / Service Details"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField
              fullWidth
              label="Product / Service Name"
              placeholder="e.g., Industrial Radar Systems"
              value={productName}
              onChange={(e) => update(setProductName)(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              label="Description"
              placeholder="Describe the product or service being researched..."
              value={productDescription}
              onChange={(e) => update(setProductDescription)(e.target.value)}
            />
          </Box>
        </Section>
      )}

      {/* ── Documents (all types) ── */}
      {/* The document library is gated on the full `research` feature, so
          research_viewer guests would only get 403s from every call here. */}
      {hasFeature("research") && (
        <Section
          icon={<DocumentIcon sx={{ fontSize: 20, color: "text.secondary" }} />}
          title="Documents"
        >
          <DocumentsPanel projectId={project.id} />
        </Section>
      )}

      {/* ── Research Tracks ── */}
      <Section
        icon={<TrackIcon sx={{ fontSize: 20, color: "text.secondary" }} />}
        title="Research Tracks"
        action={
          <Chip
            label={project.tracks?.length || 0}
            size="small"
            sx={{ height: 20, fontSize: "0.75rem" }}
          />
        }
      >
        {project.tracks && project.tracks.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {project.tracks.map((track) => (
              <Box
                key={track.id}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {track.name}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Chip
                      label={
                        TRACK_TYPE_LABELS[track.track_type] || track.track_type
                      }
                      size="small"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                    {track.status !== "active" && (
                      <Chip
                        label={track.status}
                        size="small"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">
            No research tracks yet. Add a track from the sidebar to get started.
          </Typography>
        )}
      </Section>

      {/* ── Save button ── */}
      {dirty && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProjectOverview;
